import { memo, useCallback } from 'react'

import { Box, TextField } from '@mui/material'
import { MediaAssetInsert } from '@nativewaves/nw-cloud-sdk/media'
import { useSnackbar } from 'notistack'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useShallow } from 'zustand/react/shallow'

import { useCloseHandler } from '@shared/components/MaterialUIEnhancements/Dialog'
import { SnackPanelActions, ViewSpace } from '@shared/components/Snackbar'
import {
  selectSubmissionProgressSetters,
  SubmissionProgress,
  useSubmissionProgressStore,
} from '@shared/components/SubmissionProgress'
import { DialogFormContent } from '@shared/layouts'
import { SnackPanelActionButtonLink } from 'components/LinkedMUIComponents'
import { useMediaAssetCreateMutation } from 'hooks/mutations/media/MediaAsset'
import { mediaPlatformAssetsRoute } from 'pages/NWPlatform/MediaPlatforms/MediaPlatform/Assets'
import { mediaPlatformAssetRoute } from 'pages/NWPlatform/MediaPlatforms/MediaPlatform/Assets/Asset'

type FormStructure = MediaAssetInsert

const NewDialogRaw = () => {
  const { t } = useTranslation(['common', 'domain', 'entity'])
  const { enqueueSnackbar } = useSnackbar()
  const handleClose = useCloseHandler()

  const { organizationId, environmentId, platformId } = mediaPlatformAssetsRoute.useParams()

  const { setCurrentStep, setError: setSubmissionError } = useSubmissionProgressStore(
    useShallow(selectSubmissionProgressSetters),
  )

  const assetCreateMutation = useMediaAssetCreateMutation()
  const { mutate: createAsset } = assetCreateMutation

  const { handleSubmit, control } = useForm<FormStructure>({
    defaultValues: {
      name: '',
      type: '',
    },
  })

  const handleFormSubmit = useCallback(
    (assetInsert: FormStructure) => {
      setCurrentStep({
        value: 10,
        description: t('creating', { name: assetInsert.name }),
      })

      createAsset(
        {
          mediaAssetInsert: {
            ...assetInsert,
            platformId,
          },
        },
        {
          onSuccess: (data) => {
            setCurrentStep({ value: 100, description: t('created', { name: data.name }) })

            setTimeout(() => {
              enqueueSnackbar(`${t('created', { name: data.name })}`, {
                description: `${t('domain:MediaPlatform.createdSuccessDescription')}`,
                variant: 'panel',
                persist: true,
                icon: 'success',
                Actions: ({ onClose }) => (
                  <SnackPanelActions>
                    <SnackPanelActionButtonLink
                      to={mediaPlatformAssetRoute.to}
                      params={{ organizationId, environmentId, platformId, assetId: data.id }}
                      onClick={() => onClose()}
                    >
                      <ViewSpace />
                    </SnackPanelActionButtonLink>
                  </SnackPanelActions>
                ),
              })

              handleClose()
            }, 700)
          },
          onError: (_, variables) => {
            setSubmissionError()
            setCurrentStep({
              value: 60,
              description: t('couldNotBeCreated', { name: variables.mediaAssetInsert.name }),
            })
          },
        },
      )
    },
    [
      createAsset,
      enqueueSnackbar,
      environmentId,
      handleClose,
      organizationId,
      platformId,
      setCurrentStep,
      setSubmissionError,
      t,
    ],
  )

  return (
    <DialogFormContent
      Illustration={null}
      onSubmit={handleSubmit(handleFormSubmit)}
      title={t('domain:MediaPlatform.addEntity', { entity: t('entity:publishing.asset', { count: 1 }) })}
      description={t('domain:MediaPlatform.addEntityDescription', {
        entity: t('entity:publishing.asset', { count: 1 }),
      })}
      SubmitProgressZone={
        <SubmissionProgress isSuccess={assetCreateMutation.isSuccess} isError={assetCreateMutation.isError} />
      }
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Controller
          name="name"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              autoFocus
              margin="dense"
              label={t('name')}
              type="text"
              fullWidth
              variant="standard"
              autoComplete="off"
              error={!!error}
              helperText={error?.message}
              required
            />
          )}
        />
        <Controller
          name="type"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              margin="dense"
              label={t('domain:MediaPlatform.type')}
              type="text"
              fullWidth
              variant="standard"
              autoComplete="off"
              error={!!error}
              helperText={error?.message}
              required
            />
          )}
        />
      </Box>
    </DialogFormContent>
  )
}

export const NewDialog = memo(NewDialogRaw)
